import { useApolloClient } from "@apollo/client";
import { type PropsWithChildren, useMemo } from "react";

import { useToken } from "../../../providers/token";
import { useOakStreamClient } from "../../../stream/useOakStreamClient";
import { ScoutConversationController } from "../../Customer/Scout/hooks/scoutConversationController";
import { ScoutConversationControllerContext } from "../../Customer/Scout/hooks/scoutConversationControllerContext";
import { ScoutConversationStore } from "../../Customer/Scout/hooks/scoutConversationStore";
import { staffScoutHistoryLoader, staffScoutStreamUrlBuilder } from "./scoutObserverHistoryLoader";

// Mounts an isolated ScoutConversationController for the staff observer.
// Overriding the controller context inside this subtree means useScoutConversation
// — and therefore the existing message rendering machinery — picks up the staff
// SSE URL builder and staff history query without any further plumbing. The
// store is a fresh instance so observer state can't pollute the customer
// singleton (and vice versa).
export function StaffScoutControllerProvider({ children }: PropsWithChildren) {
  const apolloClient = useApolloClient();
  const streamClient = useOakStreamClient();
  const { getIdTokenRefreshIfExpired } = useToken();

  const controller = useMemo(
    () =>
      new ScoutConversationController({
        apolloClient,
        streamClient,
        getIdTokenRefreshIfExpired,
        store: new ScoutConversationStore(),
        buildStreamUrl: staffScoutStreamUrlBuilder,
        loadHistory: staffScoutHistoryLoader,
      }),
    [apolloClient, getIdTokenRefreshIfExpired, streamClient]
  );

  return (
    <ScoutConversationControllerContext.Provider value={controller}>
      {children}
    </ScoutConversationControllerContext.Provider>
  );
}
